import axios from 'axios';
import { auth } from '../firebase';
import { formatApiError } from '../utils/apiUtils';
import { OFFICIAL_COURSES } from './courseCatalog';

const PRODUCTION_API_FALLBACK = 'https://arivunest-backend-271603019874.us-central1.run.app/api';
const isLocalRuntime =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

// API base URL - environment variable preferred.
// If env is missing in production, do NOT fall back to localhost.
const API_BASE_URL = process.env.REACT_APP_API_URL || (isLocalRuntime ? 'http://localhost:8000/api' : PRODUCTION_API_FALLBACK);

/**
 * Axios instance for the entire application.
 * Default Content-Type is JSON.
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 90000,  // 90 seconds for AI operations (Gemini activity generation)
});

/**
 * Request Interceptor: Inject Auth Token
 */
apiClient.interceptors.request.use(
  async (config) => {
    // Check localStorage for token (from backend login)
    const token = localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // If no backend token, try Firebase as fallback
    else if (auth.currentUser) {
      try {
        const firebaseToken = await auth.currentUser.getIdToken();
        config.headers.Authorization = `Bearer ${firebaseToken}`;
      } catch (error) {
        console.error("Error getting Firebase token:", error);
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor: Handle 401s and Errors
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Format error message for better UX
    if (error.response) {
      error.userMessage = formatApiError(error);
      
      if (error.response.status === 401) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        // Do not redirect to login if the user is on a public page
        const publicPaths = ['/', '/courses', '/about', '/contact', '/login', '/signup'];
        if (!publicPaths.includes(window.location.pathname)) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// --- 1. Auth API ---
export const authAPI = {
  register: (userData) => {
    return apiClient.post('/auth/register', userData);
  },

  // OAuth2 login (form-encoded)
  login: (email, password) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    return apiClient.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
  },

  // Login user
  // Sync user with backend
  syncUser: (userData) => {
    return apiClient.post('/auth/sync', userData);
  },

  // Get current user profile from backend
  getMe: () => {
    return apiClient.get('/auth/me');
  },

  // Logout user
  logout: () => {
    return apiClient.post('/auth/logout');
  },
};

// --- 2. Speech API ---
// Speech analysis feature removed — no client API provided.

// --- 3. Student API ---
export const studentAPI = {
  // Get student profile
  getProfile: () => {
    return apiClient.get('/student/profile');
  },

  // Get student stats (ZenCoins, XP) - Secured: No param needed
  getStats: () => {
    return apiClient.get(`/learning/profile`);
  },

  // Update student profile
  updateProfile: (data) => {
    return apiClient.put('/student/profile', data);
  },

  // Get student progress data
  getProgress: () => {
    return apiClient.get('/student/progress');
  },

  // Get achievements and badges
  getAchievements: () => {
    return apiClient.get('/student/achievements');
  },

  // Get course-by-course performance
  getPerformance: (params) => {
    return apiClient.get('/student/performance', { params });
  },

  // Get personalized recommendations
  getRecommendations: (limit = 6) => {
    return apiClient.get('/student/recommendations', { params: { limit } });
  },
};

// --- 3.5. Learning API (Progress, Gamification) ---
export const learningAPI = {
  // Get user profile with XP, coins, streak, level
  getProfile: () => {
    return apiClient.get('/learning/profile');
  },
  
  // Get leaderboard (top users by ZenCoins)
  getLeaderboard: (limit = 10) => {
    return apiClient.get(`/learning/leaderboard?limit=${limit}`);
  },
  
  // Complete a lesson (awards XP/coins)
  completeLesson: (courseId, lessonId, lessonTitle) => {
    return apiClient.post(`/learning/lesson/complete?course_id=${courseId}&lesson_id=${lessonId}&lesson_title=${encodeURIComponent(lessonTitle)}`);
  },
  
  // Complete an activity (quiz, speech, written)
  completeActivity: (activityType, score, courseId = null, lessonId = null) => {
    return apiClient.post(`/learning/activity/complete?activity_type=${activityType}&score=${score}${courseId ? `&course_id=${courseId}` : ''}${lessonId ? `&lesson_id=${lessonId}` : ''}`);
  },
  
  // Get user's enrolled courses with progress
  getEnrollments: () => {
    return apiClient.get('/learning/enrollments');
  },
  
  // Enroll in a course
  enrollInCourse: (courseId) => {
    return apiClient.post(`/learning/enroll/${courseId}`);
  },
  
  // Get recent activity (lesson completions)
  getRecentActivity: (limit = 10) => {
    return apiClient.get(`/learning/activity/recent?limit=${limit}`);
  },
};

// --- 4. Instructor API ---
export const instructorAPI = {
  // Get all students
  getStudents: () => {
    return apiClient.get('/instructor/students');
  },

  // Get student details
  getStudentById: (studentId) => {
    return apiClient.get(`/instructor/students/${studentId}`);
  },

  // Get a specific student's progress data
  getStudentProgress: (studentId) => {
    return apiClient.get(`/instructor/students/${studentId}/progress`);
  },

  // Get a specific student's feedback history
  getStudentFeedback: (studentId) => {
    return apiClient.get(`/instructor/students/${studentId}/feedback`);
  },

  // Add feedback for student
  addFeedback: (sessionId, feedback) => {
    return apiClient.post(`/instructor/feedback/${sessionId}`, feedback);
  },

  // Get instructor's feedback history
  getFeedbackHistory: () => {
    return apiClient.get('/instructor/feedback');
  },
};

// --- 5. Moodle API ---
export const moodleAPI = {
  getCourses: () => apiClient.get('/moodle/courses'),
  submitAssignment: (data) => apiClient.post('/moodle/submit_assignment', data),
};

// --- 6. Payments API ---
export const paymentsAPI = {
  // Create Razorpay order
  createOrder: (data) => {
    return apiClient.post('/payments/create-order', data);
  },

  // Verify payment
  verifyPayment: (data) => {
    return apiClient.post('/payments/verify', data);
  },

  // Get payment history
  getHistory: () => {
    return apiClient.get('/payments/history');
  },
};

// --- 7. Admin API ---
export const DEFAULT_ADMIN_USERS = [
  { id: 1, full_name: "Dr. S. Sabitha Shunmugapriya", designation: "Chairman & Founder, Chief Mentor", email: "dr.sabitha@arivunest.com", role: "admin", is_active: true, mobile_no: "+91 94880 00000", created_at: "2026-10-01T00:00:00Z" },
  { id: 2, full_name: "Nandhakumar Murugan", designation: "Chief Operations Officer (COO) / Head of Technology & Strategy", email: "nandhakumar@arivunest.com", role: "admin", is_active: true, mobile_no: "+91 63805 33437", created_at: "2026-10-01T00:00:00Z" },
  { id: 3, full_name: "Mohamed Irfan", designation: "Chief Technology Officer (CTO) / Head of Operations & Administration", email: "mooran@arivunest.com", role: "admin", is_active: true, mobile_no: "+91 95977 78204", created_at: "2026-10-01T00:00:00Z" },
  { id: 4, full_name: "Mrs. Jothy", designation: "Treasurer / Head of Finance Management", email: "finance@arivunest.com", role: "admin", is_active: true, mobile_no: "+91 88072 63085", created_at: "2026-10-24T00:00:00Z" },
  { id: 5, full_name: "Kani Rathnam Equality Foundation", designation: "Founding Partner & Social Pillar", email: "kanirathinamfoundation@gmail.com", role: "admin", is_active: true, mobile_no: "+91 94880 00000", created_at: "2026-10-01T00:00:00Z" },
  { id: 5, full_name: "Mrs. M. Geetha", email: "geetha.eng@kcas.edu.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10001", created_at: "2026-10-21T00:00:00Z" },
  { id: 6, full_name: "Dr. D. Lourdhu Mary, Ph.D", email: "lourdhumary@sjctnc.edu.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10002", created_at: "2026-10-21T00:00:00Z" },
  { id: 7, full_name: "Mr. Brahma Naidu Chavali", email: "brahmanaidu.chavali@ap.gov.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10003", created_at: "2026-10-22T00:00:00Z" },
  { id: 8, full_name: "Dr. R. Kumara Balaji, Ph.D", email: "kumarabalaji@ksrct.ac.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10004", created_at: "2026-10-22T00:00:00Z" },
  { id: 9, full_name: "Dr. M. Leena Chandrika, Ph.D", email: "leenachandrika@srec.ac.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10005", created_at: "2026-10-22T00:00:00Z" },
  { id: 10, full_name: "Dr. A. Balasubramanian, Ph.D", email: "balasubramanian.mba@niitm.ac.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10006", created_at: "2026-10-22T00:00:00Z" },
  { id: 11, full_name: "Ms. B. Malini", email: "malinipackiam@gmail.com", role: "instructor", is_active: true, mobile_no: "+91 63805 33437", created_at: "2026-10-23T00:00:00Z" },
  { id: 12, full_name: "Dr. T. Ezhilarasi, Ph.D", email: "ezhilarasi.mba@aimt.edu.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10008", created_at: "2026-10-23T00:00:00Z" },
  { id: 13, full_name: "Prof. M. Nallakurumban", email: "nallakurumban@aac.ac.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10009", created_at: "2026-10-25T00:00:00Z" },
  { id: 14, full_name: "Prof. E. Annal Sheeba Rani", email: "annalsheeba@jct.ac.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10010", created_at: "2026-10-25T00:00:00Z" },
  { id: 15, full_name: "Dr. Mydhili S.K., Ph.D", email: "mydhili.ece@uit.ac.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10011", created_at: "2026-10-26T00:00:00Z" },
  { id: 16, full_name: "Prof. K. Muthukumar", email: "muthukumar.mba@jsec.ac.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10012", created_at: "2026-10-26T00:00:00Z" },
  { id: 17, full_name: "Prof. M. Praveenkumar", email: "praveenkumar.mba@jsec.ac.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10013", created_at: "2026-10-26T00:00:00Z" },
  { id: 18, full_name: "Prof. T. Udhayakumar", email: "udhayakumar.cse@fet.edu.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10014", created_at: "2026-10-26T00:00:00Z" },
  { id: 19, full_name: "Prof. S. Jayaprakash", email: "jayaprakash.cse@hit.edu.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10015", created_at: "2026-10-26T00:00:00Z" },
  { id: 20, full_name: "Mr. Debabrata Roy", email: "debabrata.roy@haldia.ac.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10016", created_at: "2026-10-26T00:00:00Z" },
  { id: 21, full_name: "Dr. N. Maryanna Harris, Ph.D", email: "maryanna.eng@christ.edu.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10017", created_at: "2026-10-27T00:00:00Z" },
  { id: 22, full_name: "Dr. K. Vinoth, Ph.D", email: "vinoth.aiml@srmist.edu.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10018", created_at: "2026-10-27T00:00:00Z" },
  { id: 23, full_name: "Dr. M. S. Parkavi, Ph.D", email: "parkavi.eng@gasc.edu.in", role: "instructor", is_active: true, mobile_no: "+91 98422 10019", created_at: "2026-10-27T00:00:00Z" },
  { id: 24, full_name: "Sarah Jenkins", email: "sarah@example.com", role: "student", is_active: true, mobile_no: "+91 98422 20001", created_at: "2026-10-15T00:00:00Z" },
  { id: 25, full_name: "David Chen", email: "david@example.com", role: "student", is_active: true, mobile_no: "+91 98422 20002", created_at: "2026-10-16T00:00:00Z" },
  { id: 26, full_name: "Student Scholar", email: "student@arivunest.com", role: "student", is_active: true, mobile_no: "+91 98422 20003", created_at: "2026-10-17T00:00:00Z" }
];

export const adminAPI = {
  // Get platform stats
  getStats: async () => {
    try {
      return await apiClient.get('/admin/stats');
    } catch (e) {
      console.warn('Backend unavailable, using fallback admin stats');
      return {
        data: {
          total_users: 28,
          students: 19,
          instructors: 5,
          admins: 4,
          total_courses: OFFICIAL_COURSES.length,
          published_courses: OFFICIAL_COURSES.length,
          total_enrollments: 142,
          total_live_classes: 2
        }
      };
    }
  },

  // Get all users
  getUsers: async () => {
    try {
      const res = await apiClient.get('/admin/users');
      if (res.data && Array.isArray(res.data) && res.data.length > 0) return res;
      throw new Error('Empty users list');
    } catch (e) {
      console.warn('Backend unavailable, using fallback admin users directory');
      const cached = localStorage.getItem('arivunest_admin_users');
      if (cached) {
        try { return { data: JSON.parse(cached) }; } catch (_) {}
      }
      return { data: DEFAULT_ADMIN_USERS };
    }
  },

  // Update user role
  updateUserRole: async (userId, role) => {
    try {
      return await apiClient.put(`/admin/users/${userId}/role`, { role });
    } catch (e) {
      const cached = localStorage.getItem('arivunest_admin_users');
      let list = cached ? JSON.parse(cached) : [...DEFAULT_ADMIN_USERS];
      list = list.map(u => u.id === userId ? { ...u, role } : u);
      localStorage.setItem('arivunest_admin_users', JSON.stringify(list));
      return { data: { success: true, message: `Role updated to ${role} (saved locally)` } };
    }
  },

  // Toggle user active/inactive
  toggleUserActive: async (userId) => {
    try {
      return await apiClient.put(`/admin/users/${userId}/toggle-active`);
    } catch (e) {
      const cached = localStorage.getItem('arivunest_admin_users');
      let list = cached ? JSON.parse(cached) : [...DEFAULT_ADMIN_USERS];
      list = list.map(u => u.id === userId ? { ...u, is_active: !u.is_active } : u);
      localStorage.setItem('arivunest_admin_users', JSON.stringify(list));
      return { data: { success: true } };
    }
  },

  // Delete user
  deleteUser: async (userId) => {
    try {
      return await apiClient.delete(`/admin/users/${userId}`);
    } catch (e) {
      const cached = localStorage.getItem('arivunest_admin_users');
      let list = cached ? JSON.parse(cached) : [...DEFAULT_ADMIN_USERS];
      list = list.filter(u => u.id !== userId);
      localStorage.setItem('arivunest_admin_users', JSON.stringify(list));
      return { data: { success: true } };
    }
  },

  // Get all courses (Admin View)
  getCourses: async () => {
    try {
      const res = await apiClient.get('/admin/courses');
      if (res.data && Array.isArray(res.data) && res.data.length > 0) return res;
      throw new Error('Empty admin courses');
    } catch (e) {
      return {
        data: OFFICIAL_COURSES.map(c => ({
          id: c.id,
          title: c.title,
          instructor_name: c.instructor_name || 'Dr. S. Sabitha Shunmugapriya',
          category: c.category || 'English & Research',
          enrollment_count: c.enrollment_count || 38,
          is_published: true
        }))
      };
    }
  },

  // Get all live classes (Admin View)
  getLiveClasses: async () => {
    try {
      const res = await apiClient.get('/admin/live-classes');
      if (res.data && Array.isArray(res.data) && res.data.length > 0) return res;
      throw new Error('Empty live classes');
    } catch (e) {
      return {
        data: [
          {
            id: 1,
            title: "Executive Communication & Faculty Mentorship",
            instructor_name: "Dr. S. Sabitha Shunmugapriya",
            scheduled_at: "2026-10-30T10:00:00Z",
            status: "scheduled",
            meeting_id: "ARIVU-LIVE-01"
          },
          {
            id: 2,
            title: "UGC NET & Research Journal Publication Masterclass",
            instructor_name: "Dr. S. Sabitha Shunmugapriya",
            scheduled_at: "2026-11-05T15:30:00Z",
            status: "scheduled",
            meeting_id: "ARIVU-LIVE-02"
          }
        ]
      };
    }
  }
};

// --- 7. Courses API ---
export const coursesAPI = {
  getAll: async () => {
    try {
      const res = await apiClient.get('/courses/');
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        return res;
      }
      return { data: OFFICIAL_COURSES };
    } catch (e) {
      console.warn('Backend unavailable, serving official ARIVUNEST course catalog', e);
      return { data: OFFICIAL_COURSES };
    }
  },
  getCourse: async (id) => {
    try {
      return await apiClient.get(`/courses/${id}`);
    } catch (e) {
      const fallback = OFFICIAL_COURSES.find(c => String(c.id) === String(id));
      if (fallback) return { data: fallback };
      throw e;
    }
  },
  getById: async (id) => {
    try {
      return await apiClient.get(`/courses/${id}`);
    } catch (e) {
      const fallback = OFFICIAL_COURSES.find(c => String(c.id) === String(id));
      if (fallback) return { data: fallback };
      throw e;
    }
  },
  create: (courseData) => {
    return apiClient.post('/courses/', courseData);
  },
  update: (id, courseData) => {
    return apiClient.put(`/courses/${id}`, courseData);
  },
  delete: (id) => {
    return apiClient.delete(`/courses/${id}`);
  },
  getMyCourses: () => {
    return apiClient.get('/courses/my-courses');
  }
};

// --- 7.5. Live Classes API ---
export const liveClassesAPI = {
  getAll: () => {
    return apiClient.get('/live-classes/');
  },
  getAllIncludingUnpublished: () => {
    return apiClient.get('/live-classes/all');
  },
  getMine: () => {
    return apiClient.get('/live-classes/my');
  },
  create: (payload) => {
    return apiClient.post('/live-classes/', payload);
  },
  update: (id, payload) => {
    return apiClient.put(`/live-classes/${id}`, payload);
  },
  remove: (id) => {
    return apiClient.delete(`/live-classes/${id}`);
  },
  join: (id) => {
    return apiClient.post(`/live-classes/${id}/join`);
  },
};

// --- 8. Analytics & Student API ---
export const analyticsAPI = {
  // Get overall student progress
  getProgress: () => {
    return apiClient.get('/student/progress');
  },

  // Get achievements and badges
  getAchievements: () => {
    return apiClient.get('/student/achievements');
  },

  // Get course-by-course performance
  getPerformance: () => {
    return apiClient.get('/student/performance');
  },

  // Get learning streak
  getStreak: () => {
    return apiClient.get('/student/learning-streak');
  },

  // Get personalized recommendations
  getRecommendations: (limit = 6) => {
    return apiClient.get('/student/recommendations', { params: { limit } });
  },

  // Get trending courses
  getTrendingCourses: (limit = 5) => {
    return apiClient.get('/student/trending-courses', { params: { limit } });
  },

  // Get complete dashboard summary (all data at once)
  getDashboardSummary: () => {
    return apiClient.get('/student/dashboard-summary');
  },

  // Get notifications
  getNotifications: (limit = 20) => {
    return apiClient.get('/notifications', { params: { limit } });
  },

  // Get unread notifications only
  getUnreadNotifications: () => {
    return apiClient.get('/notifications', { params: { unread_only: true } });
  },

  // Get unread count
  getUnreadCount: () => {
    return apiClient.get('/notifications/unread-count');
  },

  // Mark notification as read
  markNotificationRead: (notificationId) => {
    return apiClient.put(`/notifications/${notificationId}/read`);
  },

  // Mark all notifications as read
  markAllNotificationsRead: () => {
    return apiClient.put('/notifications/mark-all-read');
  },

  // Delete notification
  deleteNotification: (notificationId) => {
    return apiClient.delete(`/notifications/${notificationId}`);
  },

  // Get notification stats
  getNotificationStats: () => {
    return apiClient.get('/notifications/stats');
  },

  // --- Notification Preferences ---
  // Get notification preferences (email, phone, channels)
  getNotificationPreferences: () => {
    return apiClient.get('/notifications/preferences/channels');
  },

  // Update notification preferences
  updateNotificationPreferences: (preferences) => {
    return apiClient.post('/notifications/preferences/channels', preferences);
  },

  // Test send notification (for testing)
  testSendNotification: (channel = 'in-app') => {
    return apiClient.post(`/notifications/test/send?channel=${channel}`);
  }
};

// --- 9. Notifications API ---
export const notificationsAPI = {
  // Get notifications
  getNotifications: (params) => {
    return apiClient.get('/notifications', { params });
  },

  get: (limit = 20, unreadOnly = false) => {
    return apiClient.get('/notifications', { params: { limit, unread_only: unreadOnly } });
  },

  // Get unread count
  getUnreadCount: () => {
    return apiClient.get('/notifications/unread-count');
  },

  // Mark notification as read
  markAsRead: (notificationId) => {
    return apiClient.put(`/notifications/${notificationId}/read`);
  },

  markRead: (notificationId) => {
    return apiClient.put(`/notifications/${notificationId}/read`);
  },

  // Mark all as read
  markAllAsRead: () => {
    return apiClient.put('/notifications/mark-all-read');
  },

  markAllRead: () => {
    return apiClient.put('/notifications/mark-all-read');
  },

  // Delete notification
  deleteNotification: (notificationId) => {
    return apiClient.delete(`/notifications/${notificationId}`);
  },

  delete: (notificationId) => {
    return apiClient.delete(`/notifications/${notificationId}`);
  },

  // Get stats
  getStats: () => {
    return apiClient.get('/notifications/stats');
  }
};

// Export the axios instance for custom use
export default apiClient;